import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { ACTIONS, ActionKey, ICON_SIZE, IconComponent } from './actions';
import { cx } from './cx';

type IconButtonTone = 'default' | 'primary' | 'danger' | 'onDark';

const TONES: Record<IconButtonTone, string> = {
  default: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
  primary: 'text-primary-600 hover:bg-primary-50 hover:text-primary-700',
  danger: 'text-red-500 hover:bg-red-50 hover:text-red-600',
  onDark: 'text-white/80 hover:bg-white/10 hover:text-white',
};

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Ícone e rótulo vindos do registro de ações. */
  action?: ActionKey;
  icon?: IconComponent;
  /** Obrigatório quando não há `action`: vira title + aria-label. */
  label?: string;
  tone?: IconButtonTone;
  iconClassName?: string;
  /** Com action="copy"/"copyLink": texto copiado (ícone vira Check + toast). */
  value?: string;
}

/**
 * Botão só com ícone fora de uma linha de ações: fechar modal, subir/descer, remover item de
 * formulário. 44×44 de toque no mobile (.icon-btn), compacto no desktop.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ action, icon, label, tone, iconClassName, className, type = 'button', value, onClick, ...rest }, ref) => {
    const { t } = useTranslation();
    const { copy, isCopied } = useCopyToClipboard();
    const def = action ? ACTIONS[action] : undefined;
    const copyable = (action === 'copy' || action === 'copyLink') && value !== undefined;
    const copied = copyable && isCopied();
    const Icon = copied ? Check : icon || def?.icon;
    const text = copied ? t('ui.copied') : label || (action ? t(`ui.actions.${action}`) : '');
    const resolvedTone: IconButtonTone =
      tone || (def?.variant === 'danger' ? 'danger' : def?.variant === 'primary' ? 'primary' : 'default');

    return (
      <button
        ref={ref}
        type={type}
        title={text}
        aria-label={text}
        className={cx(
          'icon-btn rounded-lg p-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
          TONES[resolvedTone],
          copied && 'text-green-600',
          className
        )}
        onClick={(event) => {
          if (copyable) copy(value as string);
          onClick?.(event);
        }}
        {...rest}
      >
        {Icon && <Icon className={iconClassName || ICON_SIZE.icon} aria-hidden="true" />}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
